/**
 * Barra fixa de `/revisao`: limpar, antecipar, resumo e iniciar.
 * Não lê a store e não navega — o pai passa tudo (`barraDeInicioContrato`).
 * A duração anunciada sai só de `estimativaDuracao`.
 */
import { useId } from "react";
import type { BarraDeInicioProps } from "./barraDeInicioContrato";
import { estimativaDuracao } from "./estimativaDuracao";
import { REVISAO_FILTER_FOCUS_RING, revisaoFilterCx } from "./filtros/tokens";
import { revisaoPrimaryActionClass } from "./revisaoSurfaces";

const formatoContagem = new Intl.NumberFormat("pt-BR");

function textoContagem(cartoes: number): string {
  const quantidade = formatoContagem.format(cartoes);
  const substantivo = cartoes === 1 ? "cartão" : "cartões";
  return `${quantidade} ${substantivo}`;
}

export default function BarraDeInicio(props: BarraDeInicioProps): JSX.Element {
  const {
    cartoes,
    aguardando = false,
    ahead,
    onAhead,
    onLimpar,
    podeLimpar,
    onIniciar,
    densa = false,
  } = props;
  const dicaAnteciparId = useId();
  const iniciarIndisponivel = aguardando || cartoes === 0;

  const handleIniciar = (): void => {
    if (aguardando || cartoes === 0) return;
    onIniciar();
  };

  const resumo = aguardando ? (
    <p className="text-sm text-ink-secondary">Carregando a revisão…</p>
  ) : cartoes === 0 ? (
    <div className="flex flex-col gap-0.5">
      <p className="text-sm font-semibold tabular-nums text-ink">{textoContagem(cartoes)}</p>
      <p className="text-sm text-ink-secondary">Esta seleção não produz cartões.</p>
    </div>
  ) : (
    <p className="text-sm font-semibold tabular-nums text-ink">
      {`${textoContagem(cartoes)} · ${estimativaDuracao(cartoes).rotulo}`}
    </p>
  );

  const botaoLimpar = (
    <button
      type="button"
      onClick={onLimpar}
      disabled={!podeLimpar}
      className={revisaoFilterCx(
        "h-9 min-w-0 flex-1 rounded-input border border-edge bg-surface px-3 text-sm font-medium text-ink-secondary",
        "hover:bg-surface-hover hover:text-ink",
        "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface disabled:hover:text-ink-secondary",
        REVISAO_FILTER_FOCUS_RING,
      )}
    >
      Limpar filtros
    </button>
  );

  const botaoAntecipar = (
    <button
      type="button"
      role="switch"
      aria-checked={ahead}
      aria-describedby={dicaAnteciparId}
      onClick={() => onAhead(!ahead)}
      className={revisaoFilterCx(
        "inline-flex h-9 shrink-0 items-center rounded-input border px-3 text-sm font-medium transition-colors",
        REVISAO_FILTER_FOCUS_RING,
        ahead
          ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
          : "border-edge bg-canvas text-ink-secondary hover:bg-surface-hover hover:text-ink",
      )}
    >
      Antecipar
    </button>
  );

  const botaoIniciar = (
    <button
      type="button"
      onClick={handleIniciar}
      disabled={iniciarIndisponivel}
      className={revisaoFilterCx(
        revisaoPrimaryActionClass,
        "shrink-0",
        "disabled:cursor-not-allowed disabled:opacity-50",
        REVISAO_FILTER_FOCUS_RING,
      )}
    >
      Iniciar revisão
    </button>
  );

  if (densa) {
    return (
      <div
        role="region"
        aria-label="Início da revisão"
        className="shrink-0 border-t border-edge bg-surface px-3 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      >
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">{resumo}</div>
          {botaoIniciar}
        </div>
        <div className="mt-2 flex items-center gap-2">
          {botaoLimpar}
          {botaoAntecipar}
        </div>
        <p id={dicaAnteciparId} className="mt-1 text-xs leading-snug text-ink-secondary">
          Inclui cartões que ainda não venceram.
        </p>
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Início da revisão"
      className="sticky bottom-0 z-20 border-t border-edge bg-surface px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:px-5"
    >
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center md:gap-3">
          <button
            type="button"
            onClick={onLimpar}
            disabled={!podeLimpar}
            className={revisaoFilterCx(
              "h-10 w-full rounded-input border border-edge bg-surface px-4 text-sm font-medium text-ink-secondary md:w-auto",
              "hover:bg-surface-hover hover:text-ink",
              "disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-surface disabled:hover:text-ink-secondary",
              REVISAO_FILTER_FOCUS_RING,
            )}
          >
            Limpar filtros
          </button>

          <div className="flex flex-col items-start gap-1 md:flex-row md:items-center md:gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={ahead}
              aria-describedby={dicaAnteciparId}
              onClick={() => onAhead(!ahead)}
              className={revisaoFilterCx(
                "inline-flex h-10 shrink-0 items-center rounded-input border px-4 text-sm font-medium transition-colors",
                REVISAO_FILTER_FOCUS_RING,
                ahead
                  ? "border-select/[0.42] bg-select/[0.14] text-select-ink"
                  : "border-edge bg-canvas text-ink-secondary hover:bg-surface-hover hover:text-ink",
              )}
            >
              Antecipar
            </button>
            <p id={dicaAnteciparId} className="text-sm leading-snug text-ink-secondary">
              Inclui cartões que ainda não venceram.
            </p>
          </div>
        </div>

        <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row md:items-center md:justify-end md:gap-4">
          {aguardando ? (
            <p className="text-sm text-ink-secondary">Carregando a revisão…</p>
          ) : cartoes === 0 ? (
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold tabular-nums text-ink">{textoContagem(cartoes)}</p>
              <p className="text-sm text-ink-secondary">Esta seleção não produz cartões.</p>
            </div>
          ) : (
            <p className="text-sm font-semibold tabular-nums text-ink">
              {`${textoContagem(cartoes)} · ${estimativaDuracao(cartoes).rotulo}`}
            </p>
          )}

          <button
            type="button"
            onClick={handleIniciar}
            disabled={iniciarIndisponivel}
            className={revisaoFilterCx(
              revisaoPrimaryActionClass,
              "w-full md:w-auto",
              "disabled:cursor-not-allowed disabled:opacity-50",
              REVISAO_FILTER_FOCUS_RING,
            )}
          >
            Iniciar revisão
          </button>
        </div>
      </div>
    </div>
  );
}
